import { useDrawerStore } from '../../../store';
import {
  useCreateData,
  useDepartmentStore,
  useFetchData,
  useUpdateData,
} from '../../../hooks';
import React, { useEffect, useState } from 'react';
import { BaseModel, FormField, PageableResponse } from '../../../types';
import { Resource } from '../../../lib/enums';
import { Spin } from 'antd';
import FormDrawer from '../../../components/ui/FormDrawer';
import ErrorModal from '../../../components/ui/modal/ErrorModal';

const DepartmentForm = ({
  api,
  moduleName,
}: {
  api: string;
  moduleName: string;
}) => {
  const drawerStore = useDrawerStore();
  const departmentStore = useDepartmentStore();
  const [spinning, setSpinning] = useState<boolean>(false);
  const [isInfoModalOpen, setInfoModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>('');

  const { data: categoryData, isLoading: isCategoryLoading } = useFetchData<
    PageableResponse<BaseModel>
  >('/product/categories', {
    resource: Resource.MIN,
    search: '',
    length: '999999',
  });
  const { data: defaultLocationData, isLoading: isDefaultLocationLoading } =
    useFetchData<PageableResponse<BaseModel>>('/location/locations', {
      resource: Resource.MIN,
      search: '',
      length: '999999',
    });

  const {
    mutate,
    isSuccess: isSuccessCreate,
    isError: isErrorCreate,
    error: errorCreate,
  } = useCreateData(api);
  const {
    mutate: mutateUpdate,
    isSuccess: isSuccessUpdate,
    isError: isErrorUpdate,
    error: errorUpdate,
  } = useUpdateData(api);

  const onFinish = (values: {
    categories: string[];
    defaultLocation: string;
  }) => {
    setSpinning(true);
    const defaultLocation: BaseModel | undefined =
      defaultLocationData?.data?.find((obj) => {
        return obj.id == values.defaultLocation;
      });

    const categoriesId: string[] = values.categories;

    const categories = categoryData?.data?.filter((item1) => {
      return categoriesId.some((item2) => item2 === item1.id);
    });

    const jsonData = {
      ...values,
      defaultLocation: defaultLocation,
      categories: categories,
    };

    if (departmentStore.data !== null) {
      const updatedData = {
        ...departmentStore.data,
        ...jsonData,
      };
      mutateUpdate({ id: departmentStore.data.id, form: updatedData });
    } else {
      mutate(jsonData);
    }
  };

  const formFields: FormField[] = [
    {
      name: 'code',
      label: 'Code',
      rules: [{ required: true, message: 'Please enter Code' }],
      disabled: departmentStore.data != null,
      defaultValue: departmentStore.data?.code,
    },
    {
      name: 'description',
      label: 'Description',
      rules: [{ required: true, message: 'Please enter description' }],
      defaultValue: departmentStore.data?.description,
    },
    {
      name: 'categories',
      label: 'Categories',
      values: categoryData?.data,
      isLoading: isCategoryLoading,
      isMultiple: true,
      type: 'select',
      defaultValue: departmentStore.data?.categories?.map((item) => item.id),
    },
    {
      name: 'defaultLocation',
      label: 'Default Location',
      rules: [{ required: true, message: 'Please select default location' }],
      values: defaultLocationData?.data,
      isMultiple: false,
      isLoading: isDefaultLocationLoading,
      type: 'select',
      allowClear: true,
      defaultValue: departmentStore.data?.defaultLocation?.id,
    },
  ];

  useEffect(() => {
    if (isSuccessCreate || isSuccessUpdate || isErrorUpdate || isErrorCreate)
      setSpinning(false);

    if (isErrorUpdate) {
      setInfoModalOpen(true);
      setErrorMessage(errorUpdate?.response?.data.message);
    }
    if (isErrorCreate) {
      setInfoModalOpen(true);
      setErrorMessage(errorCreate?.response?.data.message);
    }
  }, [
    isSuccessCreate,
    isSuccessUpdate,
    isErrorUpdate,
    isErrorCreate,
    errorCreate,
  ]);

  return (
    <>
      <Spin spinning={spinning} fullscreen />
      <FormDrawer
        isOpen={drawerStore.isOpen}
        onClose={drawerStore.onClose}
        data={departmentStore.data}
        moduleName={moduleName}
        setData={departmentStore.setData}
        onFinish={onFinish}
        formFields={formFields}
      />
      <ErrorModal
        visible={isInfoModalOpen}
        message={errorMessage}
        onClose={() => setInfoModalOpen(false)}
      />
    </>
  );
};

export default DepartmentForm;
